import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../utils/api";
import { routes } from "../utils/utils.routes";
import { isTenderContent } from "../utils/tenders";
import { PageSkeleton } from "../components/PageSkeleton";
import { PageHero } from "../components/subcomponents/PageHero";
import { FiDownload, FiCalendar, FiFileText } from "react-icons/fi";
import heroImage from "../assets/images/hero-image.png";
import "../styles/events.scss";

const DownloadI = FiDownload as any;
const CalI = FiCalendar as any;
const FileI = FiFileText as any;

type Tender = {
  id: number;
  slug: string;
  title: string;
  excerpt?: string;
  content?: string;
  category?: string;
  coverImage?: string;
  eventDate?: string;
  publishedAt?: string;
  createdAt?: string;
  location?: string;
};

function extractAttachment(item: Tender): string | null {
  if (item.location && /^https?:\/\//i.test(item.location)) return item.location;
  const match = (item.content || "").match(/href="(https?:\/\/[^"]+\.(pdf|PDF)[^"]*)"/);
  return match ? match[1] : null;
}

function isOpen(deadline?: string): boolean {
  if (!deadline) return true;
  return new Date(deadline).getTime() >= Date.now();
}

async function fetchTenders(): Promise<Tender[]> {
  const res = await apiGet("/contents", { limit: 200 });
  return (res.rows || []).filter(isTenderContent);
}

export const TendersPage: React.FC = () => {
  const [items, setItems] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [statusFilter] = useState<"all" | "open" | "closed">("all");

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    fetchTenders()
      .then(setItems)
      .catch(() => {
        setLoadError(true);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const list = [...items].sort((a, b) => {
      const da = new Date(a.eventDate || a.publishedAt || a.createdAt || 0).getTime();
      const db = new Date(b.eventDate || b.publishedAt || b.createdAt || 0).getTime();
      return db - da;
    });
    if (statusFilter === "all") return list;
    if (statusFilter === "open") return list.filter((t) => isOpen(t.eventDate));
    return list.filter((t) => !isOpen(t.eventDate));
  }, [items, statusFilter]);

  return (
    <div className="tenders-page" data-testid="tenders-page">
      <PageHero
        imageCover={heroImage}
        eyebrow="Marchés publics"
        title="Appels d'offres"
        lead="Consultez les avis et dossiers de consultation publiés par l'ULPGL. Documents PDF ou image."
        tall
        breadcrumbs={[
          { label: "Accueil", href: routes.HOME },
          { label: "Événements", href: routes.EVENTS },
          { label: "Appels d'offres" },
        ]}
      />

      <div className="padding-x is-layout-constrained">
        {/* <div className="events-toolbar" style={{ marginBottom: "1.25rem" }}>
          {(["all", "open", "closed"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`page-filter-btn${statusFilter === f ? " is-active" : ""} has-primary-color text-white`}
              onClick={() => setStatusFilter(f)}
            >
              {f === "open" ? "En cours" : f === "closed" ? "Clôturés" : "Tous"}
            </button>
          ))}
        </div> */}

        {loading ? (
          <PageSkeleton />
        ) : loadError ? (
          <p className="events-empty">
            Impossible de charger les appels d&apos;offres. Vérifiez la connexion à l&apos;API.
          </p>
        ) : filtered.length === 0 ? (
          <p className="events-empty">
            {items.length === 0
              ? "Aucun appel d'offres publié pour le moment."
              : "Aucun résultat pour ce filtre. Essayez « Tous »."}
          </p>
        ) : (
          <div className="tenders-grid" data-testid="tenders-list">
            {filtered.map((t) => {
              const open = isOpen(t.eventDate);
              const attachment = extractAttachment(t);
              return (
                <article
                  key={t.id}
                  className={`tender-card${open ? "" : " is-closed"}`}
                  data-testid={`tender-card-${t.slug}`}
                >
                  <div>
                    <span className={`tender-status tender-status--${open ? "open" : "closed"} mb-4`}>
                      {open ? "En cours" : "Clôturé"}
                    </span>
                    <h3 style={{ marginTop: 12 }}>
                      <Link to={`${routes.ARTICLESDETAILS}/${t.slug}`}>{t.title}</Link>
                    </h3>
                    {t.excerpt && <p className="tender-card__excerpt">{t.excerpt}</p>}
                    <div className="tender-card__meta">
                      {t.eventDate && (
                        <span>
                          <CalI size={13} />
                          {" "}
                          Date limite :{" "}
                          {new Date(t.eventDate).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      )}
                      <span>
                        <FileI size={13} />
                        {" "}
                        Publié le{" "}
                        {new Date(t.publishedAt || t.createdAt || "").toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </div>
                  <div className="tender-card__actions">
                    {attachment && (
                      <a
                        href={attachment}
                        target="_blank"
                        rel="noreferrer"
                        className="tender-download"
                        data-testid={`tender-download-${t.id}`}
                      >
                        <DownloadI size={14} /> Télécharger
                      </a>
                    )}
                    <Link
                      to={`${routes.ARTICLESDETAILS}/${t.slug}`}
                      style={{ fontSize: 13, color: "#041797", fontWeight: 600 }}
                    >
                      Lire l&apos;avis →
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
